import React from 'react'
import PropTypes from 'prop-types'
import { DataBlock, FlowGrid, Paragraph } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getPersonName } from '@fs/zion-sls'

/**
 * Read-only list of the other people in the viewed person's group.
 *
 * Each heading is that person's relationship to the viewed person, not to the group's primary
 * person, so the same group reads differently from each member's panel.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.relationships Array of relationship field objects. Each item should include:
 *   - {string} id          Unique identifier (used as React key)
 *   - {string} label       Localized relationship label (e.g., 'Father', 'Mother')
 *   - {string} value       Related person's display name
 * @param {Object} props.person The SLS person object for focused person.
 * @param {boolean} props.reviewed Whether the card is in reviewed state (true) or pending state (false).
 * @param {boolean} props.isInReviewProcess Indicates if the parent panel is in review process mode for newly added people.
 * @param {boolean} [props.noGroup=false] Whether the person is in no group at all.
 * @returns {JSX.Element}
 */
const RelationshipsReviewList = ({ relationships, person, reviewed, isInReviewProcess, noGroup = false }) => {
  const [t] = useTranslation()

  // Unreviewed and empty renders nothing, signalling an incomplete section — unless the parent is
  // walking newly added people through review.
  if (reviewed === false && !isInReviewProcess) {
    return null
  }

  // In no group at all, unlike a one-person group: nothing to edit, so explain the routes into one.
  if (noGroup) {
    return (
      <FlowGrid orientation="vertical" columns={1} gutters="xxs">
        <Paragraph size="sm" data-testid="relationships-no-group-message">
          <strong>
            {t('relationships-card.not-in-group', '{personName} is not in a group.', {
              personName: getPersonName({ person }),
            })}
          </strong>
        </Paragraph>
        <Paragraph size="sm">
          {t(
            'relationships-card.not-in-group-instructions',
            'To add to an existing group, open a person in that group and edit Relationships. To start a new group, use New group.'
          )}
        </Paragraph>
      </FlowGrid>
    )
  }

  return (
    <FlowGrid orientation="vertical" columns={1} gutters="xxs">
      {relationships.length === 0 && (
        <Paragraph secondary size="sm" data-testid="relationships-no-other-members">
          {t('relationships-card.no-other-members', 'No additional people in this group.')}
        </Paragraph>
      )}

      {relationships.length > 0 &&
        relationships.map((rel) => (
          <DataBlock key={rel?.person?.id || rel?.id} heading={rel?.label} data={rel?.value} />
        ))}
    </FlowGrid>
  )
}

RelationshipsReviewList.propTypes = {
  relationships: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  person: PropTypes.shape({ id: PropTypes.string }),
  reviewed: PropTypes.bool,
  isInReviewProcess: PropTypes.bool,
  noGroup: PropTypes.bool,
}

RelationshipsReviewList.defaultProps = {
  relationships: [],
  noGroup: false,
}

export default RelationshipsReviewList
